import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Creator } from './useCreators';

interface UseSyncedCreatorsProps {
  platform?: 'tiktok' | 'instagram' | 'youtube' | 'all';
  searchTerm?: string;
}

export const useSyncedCreators = ({ platform = 'all', searchTerm }: UseSyncedCreatorsProps = {}) => {
  return useQuery({
    queryKey: ['synced-creators', platform, searchTerm],
    queryFn: async (): Promise<Creator[]> => {
      let query = supabase
        .from('creators')
        .select('*')
        .order('follower_count', { ascending: false });

      if (platform !== 'all') {
        query = query.eq('platform', platform);
      }

      if (searchTerm && searchTerm.trim().length > 0) {
        query = query.or(`username.ilike.%${searchTerm}%,display_name.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query.limit(50);

      if (error) throw error;

      return (data || []).map(row => ({
        id: row.id,
        username: row.username,
        display_name: row.display_name,
        avatar_url: row.avatar_url,
        follower_count: row.follower_count,
        following_count: row.following_count,
        video_count: row.video_count,
        like_count: row.like_count,
        platform: row.platform as 'tiktok' | 'instagram' | 'youtube',
        is_verified: row.is_verified,
        bio: row.bio,
        external_links: row.external_links as any,
        audience_demographics: row.audience_demographics as any,
        category: row.category,
        region: row.region,
        email: row.email,
        contact_phone: row.contact_phone,
        business_category: row.business_category,
        profile_url: row.profile_url,
      }));
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};
